import { useEffect, useCallback, useState } from 'react'
import { type Player as BrightcovePlayerTypeI } from '@fs/zion-video'
import { type YouTubePlayer as YoutubePlayerType } from 'react-youtube'
import { VideoProviderType } from '@fs/zion-rootstech'
import { second, watchHistoryConfig } from '../../../../../config'
import { parseMsToISO } from '../../../../helpers'
import { useWatchHistoryContext } from '../../../../../providers/WatchHistoryProvider'

interface BrightcovePlayerType extends BrightcovePlayerTypeI {
  ended?: () => boolean
}
type VideoPlayer = BrightcovePlayerType | YoutubePlayerType

// different from the shared video duration method because this has the player object
const getVideoDuration = (player: VideoPlayer, videoProviderType: VideoProviderType | undefined): number => {
  if (videoProviderType === VideoProviderType.Brightcove) {
    return (player as BrightcovePlayerType).duration()
  }
  if (videoProviderType === VideoProviderType.YouTube) {
    return Number.parseInt((player as YoutubePlayerType).getDuration(), 10)
  }
  return -Infinity
}

type UseWatchEventArgs = {
  videoProviderType: VideoProviderType | undefined
  sessionId: string | undefined
  startTime: number
}

export default function useWatchEvent({ videoProviderType, sessionId, startTime }: UseWatchEventArgs): {
  trackHistoryEvent: (event: VideoPlayer | { target: YoutubePlayerType }, isCompleted?: boolean) => void
  setVideoPlayer: (player: VideoPlayer) => void
} {
  const { updateEntry } = useWatchHistoryContext()
  const [videoPlayer, setVideoPlayer] = useState<VideoPlayer>()

  const trackHistoryEvent = useCallback(
    (event: VideoPlayer | { target: YoutubePlayerType }, isCompleted = false) => {
      if (!sessionId) return
      const player: VideoPlayer = (event as { target: YoutubePlayerType }).target ?? event

      let seekTime = 0
      if (videoProviderType === VideoProviderType.Brightcove) {
        seekTime = (player as BrightcovePlayerType).currentTime()
      } else if (videoProviderType === VideoProviderType.YouTube) {
        seekTime = Number.parseInt((player as YoutubePlayerType).getCurrentTime(), 10)
      }
      const duration = getVideoDuration(player, videoProviderType)
      // if seek time longer than a reasonable length of a session
      if (startTime && seekTime > 7200) {
        seekTime -= startTime
      }
      const isProbablyCompleted = (seekTime / duration) * 100 >= watchHistoryConfig.COMPLETED_THRESHOLD

      seekTime > 0 &&
        updateEntry({
          sessionId,
          completed: isCompleted || isProbablyCompleted,
          position: parseMsToISO(seekTime * second),
        })
    },
    [sessionId, startTime, updateEntry, videoProviderType]
  )

  const onUnload = useCallback(() => {
    if (
      // the player never loaded, so no need to pause it
      !videoPlayer ||
      // video.js (brightcove) uses ended()
      (videoPlayer as BrightcovePlayerType).ended?.() ||
      // youtube keeps a player state, 0 === ended
      (videoPlayer as YoutubePlayerType).getPlayerState?.() === 0
    ) {
      return
    }
    trackHistoryEvent(videoPlayer)
  }, [trackHistoryEvent, videoPlayer])

  // When we leave the app
  useEffect(() => {
    window.addEventListener('beforeunload', onUnload)
    return () => {
      window.removeEventListener('beforeunload', onUnload)
    }
  }, [onUnload])

  // when we stay in the app, but leave the page
  useEffect(() => {
    return () => {
      onUnload()
    }
  }, [onUnload])

  return { trackHistoryEvent, setVideoPlayer }
}
